import {
  CartDealResult,
  CartLineInput,
  Deal,
  DealAction,
  DealAllocation,
  DealBreakdownEntry,
  DealCondition,
  ItemDealResult,
} from './types'

// ---------------------------------------------------------------------------
// Eligibility helper: isDealActive
//
// Single place for ALL deal eligibility checks.
// Checks is_active, starts_at, and expires_at.
// ---------------------------------------------------------------------------

export function isDealActive(deal: Deal): boolean {
  const now = new Date()
  if (
    !deal.is_active ||
    (deal.starts_at && new Date(deal.starts_at) > now) ||
    (deal.expires_at && new Date(deal.expires_at) < now)
  )
    return false
  return true
}

// ---------------------------------------------------------------------------
// Function 1: calculateDiscount
//
// Single place for ALL discount math. Returns the discount AMOUNT.
// Every discount type, every modifier — one function.
// ---------------------------------------------------------------------------

export function calculateDiscount(unitPrice: number, quantity: number, action: DealAction): number {
  const lineTotal = unitPrice * quantity
  const val = action.discount_value || 0
  let discount = 0

  switch (action.discount_type) {
    case 'percentage':
      discount = lineTotal * (val / 100)
      break

    case 'fixed_amount':
      // Per-unit amount × quantity. Callers that want a flat amount (cart_total /
      // bundle) pass quantity=1.
      discount = Math.min(val * quantity, lineTotal)
      break

    case 'bxgy': {
      const buyQty = action.buy_quantity || 0
      const freeQty = action.free_quantity || 0
      if (!buyQty || !freeQty) break
      // BOGOF model: need buyQty + freeQty items for one complete set
      const setSize = buyQty + freeQty
      const earnedSets = Math.floor(quantity / setSize)
      discount = earnedSets * freeQty * unitPrice
      break
    }

    case 'free_shipping':
    default:
      break
  }

  // Universal cap — applies to ALL discount types
  if (action.max_discount_amount != null && action.max_discount_amount > 0) {
    discount = Math.min(discount, action.max_discount_amount)
  }

  return discount
}

// ---------------------------------------------------------------------------
// Bundle helper: computeBundleDiscount
//
// Convenience wrapper for bundle modals/sections.
// Pass the bundle total and the deal — returns the discount amount via F1.
// ---------------------------------------------------------------------------

export function computeBundleDiscount(bundleTotal: number, deal: Deal): number {
  return calculateDiscount(bundleTotal, 1, deal.action)
}

// ---------------------------------------------------------------------------
// Function 2: computeLineItemDealPrice
//
// Applies ALL applicable deals to ONE line item.
// Handles priority, expiry, free_shipping, stacking, stop_further_rules.
// Skips bundle deals (bundles are multi-product — handled by evaluateCart
// and computeBundleDiscount).
// Calls F1 for every deal.
// ---------------------------------------------------------------------------

export const isBundleDeal = (deal: Deal) =>
  deal.conditions.some((c) => c.field === 'component_ids' && Array.isArray(c.value) && c.value?.length)

export const isFreeShippingDeal = (deal: Deal) => deal.action.discount_type === 'free_shipping'

export function computeLineItemDealPrice(unitPrice: number, quantity: number, deals: Deal[]) {
  const lineTotal = unitPrice * quantity
  let totalDiscount = 0
  let runningUnitPrice = unitPrice

  const sorted = sortDealsByPriority(deals)

  for (const deal of sorted) {
    if (!isDealActive(deal)) continue
    if (isBundleDeal(deal)) continue
    if (deal.action.discount_type === 'free_shipping') continue
    // Cross-product BXGY: free item is a different product — skip here,
    // handled by cross-product path in CartTable/useDealTotals
    if (deal.action.discount_type === 'bxgy' && deal.action.free_component?.id) continue

    const discount = calculateDiscount(runningUnitPrice, quantity, deal.action)
    totalDiscount += discount

    const runningTotal = Math.max(runningUnitPrice * quantity - discount, 0)
    runningUnitPrice = quantity > 0 ? runningTotal / quantity : 0

    if (deal.stop_further_rules) break
  }

  totalDiscount = Math.min(totalDiscount, lineTotal)
  const adjustedTotal = Math.round((lineTotal - totalDiscount) * 100) / 100
  const adjustedUnitPrice = quantity > 0 ? Math.round((adjustedTotal / quantity) * 100) / 100 : unitPrice

  return {
    adjustedUnitPrice,
    adjustedTotal,
    totalDiscount: Math.round(totalDiscount * 100) / 100,
  }
}

export function hasCartConditions(deal: Deal): boolean {
  const conditions = deal.conditions ?? []
  return conditions.some((c) => ['cart_subtotal', 'cart_quantity', 'item_quantity'].includes(c.field))
}

// ---------------------------------------------------------------------------
// Filter: filterProductLevelDeals
//
// Strips cart_total and bundle_total deals that are meaningless at the
// single-product level (PDP / tile / badge). Their discount depends on
// the full cart context which does not exist on individual product views.
// ---------------------------------------------------------------------------

export function filterProductLevelDeals(deals: Deal[]): Deal[] {
  return deals.filter((d) => {
    const applyTo = d.action?.apply_to
    return applyTo === 'each_item' && !hasCartConditions(d)
  })
}

// ---------------------------------------------------------------------------
// Function 3: computeDealPrice
//
// Convenience wrapper for PDP / tiles / badges.
// Returns adjusted unit price for qty = 1.
// ---------------------------------------------------------------------------

export function computeDealPrice(tradePrice: number, deals: Deal[]): number {
  return computeLineItemDealPrice(tradePrice, 1, deals).adjustedUnitPrice
}

// ---------------------------------------------------------------------------
// Helper: computeDiscountPercent
// ---------------------------------------------------------------------------

export function computeDiscountPercent(originalPrice: number, finalPrice: number): number {
  if (!originalPrice || originalPrice <= 0) return 0
  return (originalPrice - finalPrice) / originalPrice
}

// ---------------------------------------------------------------------------
// Cart-level condition helpers (private)
// ---------------------------------------------------------------------------

interface CartContext {
  subtotal: number
  totalQty: number
  items: CartLineInput[]
  componentIdSet: Set<number>
  categorySet: Set<string>
}

function compareNumeric(actual: number, operator: string, value: unknown): boolean {
  const num = Number(value)
  switch (operator) {
    case 'gte':
      return actual >= num
    case 'lte':
      return actual <= num
    case 'eq':
      return actual === num
    case 'between': {
      const arr = Array.isArray(value) ? value.map(Number) : [0, 0]
      return actual >= arr[0] && actual <= arr[1]
    }
    default:
      return true
  }
}

function checkCondition(cond: DealCondition, cart: CartContext): boolean {
  const { field, operator, value } = cond

  if (field === 'cart_subtotal') {
    return compareNumeric(cart.subtotal, operator, value)
  }
  if (field === 'cart_quantity') {
    return compareNumeric(cart.totalQty, operator, value)
  }
  if (field === 'component_id' || field === 'component_ids') {
    const ids = Array.isArray(value) ? value.map(Number) : [Number(value)]
    if (operator === 'includes_all') return ids.every((id) => cart.componentIdSet.has(id))
    if (operator === 'includes_any') return ids.some((id) => cart.componentIdSet.has(id))
    if (operator === 'eq') return ids.some((id) => cart.componentIdSet.has(id))
    if (operator === 'in') return ids.some((id) => cart.componentIdSet.has(id))
    return ids.some((id) => cart.componentIdSet.has(id))
  }
  if (field === 'item_quantity') {
    return cart.items.some((i) => compareNumeric(i.quantity, operator, value))
  }
  if (field === 'component_category') {
    const cats = Array.isArray(value) ? value : [value]
    return cats.some((cat) => cart.categorySet.has(String(cat)))
  }
  return true
}

function checkAllConditions(conditions: DealCondition[], cart: CartContext): boolean {
  return conditions.every((c) => checkCondition(c, cart))
}

function extractProductScope(conditions: DealCondition[]): { componentIds: Set<number>; categories: string[] } {
  const componentIds = new Set<number>()
  const categories: string[] = []

  for (const cond of conditions) {
    if (cond.field === 'component_id' || cond.field === 'component_ids') {
      const ids = Array.isArray(cond.value) ? cond.value : [cond.value]
      ids.forEach((id) => componentIds.add(Number(id)))
    }
    if (cond.field === 'component_category') {
      const cats = Array.isArray(cond.value) ? cond.value : [cond.value]
      categories.push(...cats.map(String))
    }
  }

  return { componentIds, categories }
}

function getDiscountTargetItems(conditions: DealCondition[], allItems: CartLineInput[]): CartLineInput[] {
  const { componentIds, categories } = extractProductScope(conditions)

  if (componentIds.size === 0 && categories.length === 0) {
    return allItems
  }

  return allItems.filter((i) => componentIds.has(i.componentId) || categories.includes(i.category || ''))
}

export const getBundleComponentCodes = (deal: Deal): number[] =>
  (deal.conditions.find((c) => c.field === 'component_ids')?.value ?? []) as number[]

/** Stable ordering: priority ascending, then deal id ascending for deterministic stacking. */
export function sortDealsByPriority(deals: Deal[]): Deal[] {
  return [...deals].sort((a, b) => (a.priority || 0) - (b.priority || 0) || a.id - b.id)
}

// ---------------------------------------------------------------------------
// evaluateCart
//
// Cart-level deal evaluation. Handles:
// - isDealActive (is_active + starts_at + expires_at)
// - Priority sorting + stop_further_rules
// - Per-distributor filtering
// - ALL conditions (component_id, component_ids, component_category,
//   cart_subtotal, cart_quantity, item_quantity) with AND logic
// - ALL discount types: percentage, fixed_amount, fixed_price, bxgy, free_shipping
// - Cross-product BXGY: threshold model (buy X of A, get Y of B free)
// - Same-product BXGY: BOGOF model (matches F1)
// - apply_to: each_item / cart_total / bundle_total
// - Compound/additive stacking with per-deal override
// - max_discount_amount cap on ALL types
// ---------------------------------------------------------------------------

export function evaluateCart(items: CartLineInput[], deals: Deal[]): CartDealResult {
  const sorted = sortDealsByPriority(deals)

  let discountTotal = 0
  const freeShipping: string[] = []
  const breakdown: DealBreakdownEntry[] = []
  const allocations: DealAllocation[] = []

  // Track remaining price per item for compound stacking
  const remainingPrices = new Map<CartLineInput, number>()
  // Track accumulated discount per item for itemDiscounts output
  const itemDiscountAccum = new Map<CartLineInput, number>()
  for (const item of items) {
    remainingPrices.set(item, item.price)
    itemDiscountAccum.set(item, 0)
  }

  const addItemDiscount = (item: CartLineInput, amount: number) => {
    itemDiscountAccum.set(item, (itemDiscountAccum.get(item) || 0) + amount)
  }

  const addAllocation = (
    dealId: number,
    amount: number,
    target: DealAllocation['target'],
    code: string | null = null
  ) => {
    const rounded = Math.round(amount * 100) / 100
    if (rounded <= 0) return
    allocations.push({ dealId, code, amount: rounded, target })
  }

  for (const deal of sorted) {
    if (!isDealActive(deal)) continue

    const distItems = deal.distributor ? items.filter((i) => i.distributor === deal.distributor) : items
    if (!distItems.length) continue

    const subtotal = distItems.reduce((s, i) => s + (remainingPrices.get(i) || i.price) * i.quantity, 0)

    const totalQty = distItems.reduce((s, i) => s + i.quantity, 0)
    const componentIdSet = new Set<number>(distItems.map((i) => i.componentId))
    const categorySet = new Set<string>(distItems.map((i) => i.category || ''))
    const cart: CartContext = { subtotal, totalQty, items: distItems, componentIdSet, categorySet }

    const conditions = deal.conditions ?? []
    if (!checkAllConditions(conditions, cart)) continue

    if (isFreeShippingDeal(deal)) {
      freeShipping.push(deal.distributor ?? '')
      continue
    }

    // Handle bundles
    if (isBundleDeal(deal)) {
      const bundleCodes = getBundleComponentCodes(deal)
      if (!bundleCodes.length) continue
      const bundleItems = distItems.filter((i) => bundleCodes.includes(i.componentId))
      if (!bundleItems.length) continue
      const effectiveBundleItems = bundleItems.map((i) => ({ ...i, price: remainingPrices.get(i) || i.price }))

      const bundleTotal = effectiveBundleItems.reduce((s, i) => s + i.price * i.quantity, 0)
      const applyTo = deal.action.apply_to

      let discount = 0
      if (applyTo === 'each_item') {
        for (const item of effectiveBundleItems) {
          discount += calculateDiscount(item.price, item.quantity, deal.action)
        }
      } else if (applyTo === 'cart_total') {
        discount = calculateDiscount(subtotal, 1, deal.action)
      } else {
        // bundle_total (default for bundles)
        discount = calculateDiscount(bundleTotal, 1, deal.action)
      }

      if (discount > 0) {
        discountTotal += discount
        breakdown.push({
          dealId: deal.id,
          title: deal.title,
          discount: Math.round(discount * 100) / 100,
          supplier: deal.distributor || '',
        })

        if (bundleTotal > 0) {
          for (const item of bundleItems) {
            const currentPrice = remainingPrices.get(item) || item.price
            const itemTotal = currentPrice * item.quantity
            const itemShare = (itemTotal / bundleTotal) * discount
            addItemDiscount(item, itemShare)
            addAllocation(deal.id, itemShare, 'line_item', item.code)
            const newTotal = Math.max(itemTotal - itemShare, 0)
            remainingPrices.set(item, item.quantity > 0 ? newTotal / item.quantity : 0)
          }
        } else {
          addAllocation(deal.id, discount, 'order')
        }
      }

      if (deal.stop_further_rules) break
      continue
    }

    // Get target items (scoped by product/category conditions)
    const targetItems = getDiscountTargetItems(conditions, distItems)
    const effectiveItems = targetItems.map((i) => ({ ...i, price: remainingPrices.get(i) || i.price }))

    const targetSubtotal = effectiveItems.reduce((s, i) => s + i.price * i.quantity, 0)

    let discount = 0

    if (deal.action.discount_type === 'bxgy') {
      const buyQty = deal.action.buy_quantity || 0
      const freeQty = deal.action.free_quantity || 0
      if (buyQty && freeQty) {
        const freeComponent = deal.action.free_component

        const buyItems = effectiveItems.filter((i) => i.componentId !== freeComponent?.id)
        const isCrossProduct = freeComponent?.id && buyItems.length > 0

        if (isCrossProduct) {
          const searchPool = distItems.map((i) => ({ ...i, price: remainingPrices.get(i) || i.price }))

          const freeItem = searchPool.find((i) => i.componentId === freeComponent?.id)
          if (freeItem) {
            const totalBuyQty = searchPool
              .filter((i) => i.componentId !== freeComponent?.id)
              .reduce((s, i) => s + i.quantity, 0)
            const earnedSets = Math.floor(totalBuyQty / buyQty)
            const freeUnits = Math.min(earnedSets * freeQty, freeItem.quantity)
            discount = freeUnits * freeItem.price
            // Attribute the BXGY discount to the free item
            const originalFreeItem = items.find(
              (i) => i.componentId === freeItem.componentId && i.distributor === freeItem.distributor
            )
            if (originalFreeItem) {
              addItemDiscount(originalFreeItem, discount)
              addAllocation(deal.id, discount, 'line_item', originalFreeItem.code)
            }
          }
        } else {
          const setSize = buyQty + freeQty
          for (let idx = 0; idx < effectiveItems.length; idx++) {
            const item = effectiveItems[idx]
            const earnedSets = Math.floor(item.quantity / setSize)
            const itemDiscount = earnedSets * freeQty * item.price
            discount += itemDiscount
            addItemDiscount(targetItems[idx], itemDiscount)
            addAllocation(deal.id, itemDiscount, 'line_item', targetItems[idx].code)
          }
        }
        if (deal.action.max_discount_amount != null && deal.action.max_discount_amount > 0) {
          discount = Math.min(discount, deal.action.max_discount_amount)
        }
      }
    } else {
      const applyTo =
        deal.action.apply_to || (deal.action.discount_type === 'fixed_amount' ? 'cart_total' : 'each_item')

      if (applyTo === 'cart_total') {
        discount = calculateDiscount(targetSubtotal, 1, deal.action)
      } else {
        for (const item of effectiveItems) {
          discount += calculateDiscount(item.price, item.quantity, deal.action)
        }
      }
    }

    if (discount > 0) {
      discountTotal += discount
      breakdown.push({
        dealId: deal.id,
        title: deal.title,
        discount: Math.round(discount * 100) / 100,
        supplier: deal.distributor || '',
      })

      // Attribute discount to items and update remaining prices for compound stacking
      // (Skip for BXGY — already attributed above)
      // cart_total / bundle_total: update running prices for stacking but do NOT put
      // amounts in itemDiscounts — those appear only in the order summary.
      // each_item: attribute the actual per-line discount (not a proportional share of
      // the total — fixed_amount £10 off each must stay £10/line on unequal prices).
      if (deal.action.discount_type !== 'bxgy' && targetSubtotal > 0) {
        const dealApplyTo =
          deal.action.apply_to || (deal.action.discount_type === 'fixed_amount' ? 'cart_total' : 'each_item')
        const isCartLevelApplyTo = dealApplyTo === 'cart_total' || dealApplyTo === 'bundle_total'
        if (isCartLevelApplyTo) {
          addAllocation(deal.id, discount, 'order')
        }
        for (const item of targetItems) {
          const currentPrice = remainingPrices.get(item) || item.price
          const itemTotal = currentPrice * item.quantity
          const itemDiscount = isCartLevelApplyTo
            ? (itemTotal / targetSubtotal) * discount
            : calculateDiscount(currentPrice, item.quantity, deal.action)
          if (!isCartLevelApplyTo) {
            addItemDiscount(item, itemDiscount)
            addAllocation(deal.id, itemDiscount, 'line_item', item.code)
          }

          const newTotal = Math.max(itemTotal - itemDiscount, 0)
          remainingPrices.set(item, item.quantity > 0 ? newTotal / item.quantity : 0)
        }
      } else if (deal.action.discount_type !== 'bxgy') {
        // targetSubtotal is 0 but we still need to track remaining prices
        for (const item of targetItems) {
          remainingPrices.set(item, 0)
        }
      }

      // Update remaining prices for BXGY compound stacking
      if (deal.action.discount_type === 'bxgy' && targetSubtotal > 0) {
        for (const item of targetItems) {
          const currentPrice = remainingPrices.get(item) || item.price
          const itemTotal = currentPrice * item.quantity
          const itemShare = (itemTotal / targetSubtotal) * discount
          const newTotal = Math.max(itemTotal - itemShare, 0)
          remainingPrices.set(item, item.quantity > 0 ? newTotal / item.quantity : 0)
        }
      }
    }

    if (deal.stop_further_rules) break
  }

  // Build per-item discount map keyed by "code:distributor"
  const itemDiscounts = new Map<string, ItemDealResult>()
  for (const item of items) {
    const key = `${item.code}:${item.distributor}`
    const totalItemDiscount = Math.min(itemDiscountAccum.get(item) || 0, item.price * item.quantity)
    if (totalItemDiscount <= 0) continue

    const lineTotal = item.price * item.quantity
    const adjustedTotal = Math.round((lineTotal - totalItemDiscount) * 100) / 100
    const adjustedUnitPrice = item.quantity > 0 ? Math.round((adjustedTotal / item.quantity) * 100) / 100 : item.price

    const existing = itemDiscounts.get(key)
    if (existing) {
      existing.totalDiscount = Math.round((existing.totalDiscount + totalItemDiscount) * 100) / 100
      existing.adjustedTotal = Math.round((existing.adjustedTotal - totalItemDiscount) * 100) / 100
      existing.adjustedUnitPrice =
        item.quantity > 0 ? Math.round((existing.adjustedTotal / item.quantity) * 100) / 100 : item.price
    } else {
      itemDiscounts.set(key, {
        totalDiscount: Math.round(totalItemDiscount * 100) / 100,
        adjustedUnitPrice,
        adjustedTotal,
      })
    }
  }

  return {
    discountTotal: Math.round(discountTotal * 100) / 100,
    freeShipping,
    breakdown,
    itemDiscounts,
    allocations,
  }
}
